import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TabIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { financeLegacyService, FinanceLegacyStats2 } from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

const EXPENSE_COLOR = '#d32f2f';
const PROFIT_COLOR = '#2e7d32';

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('uk-UA', { maximumFractionDigits: 2 });

// "Статистика 2" (2026-08-21, за проханням користувача) - "логіка профіту
// старої системи": по роках витрати (entries, джерело "Дима") vs профіт
// (той самий journal, те саме джерело) - загальна кількість вкладень vs
// виведених коштів, все в USD.
export const FinanceLegacyStats2Tab: React.FC = () => {
  const [stats, setStats] = useState<FinanceLegacyStats2 | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeLegacyService.getStats2()
      .then(setStats)
      .catch(() => toastService.error('Не вдалося завантажити статистику.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box data-i-doc={TabIndices.DEV_FINANCE_LEGACY_STATS2}>
      <PageHint iDoc={TabIndices.DEV_FINANCE_LEGACY_STATS2} />

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stats || stats.byYear.length === 0 ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Немає даних.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 4, mb: 3, mt: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Всього вкладено (витрати)</Typography>
              <Typography variant="h5" color="error.main">{fmt(stats.totalExpenseUsd)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Всього виведено (профіт)</Typography>
              <Typography variant="h5" color="success.main">{fmt(stats.totalProfitUsd)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Загальний баланс</Typography>
              <Typography variant="h5" color={stats.totalDiffUsd >= 0 ? 'success.main' : 'error.main'}>
                {fmt(stats.totalDiffUsd)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Вкладення (витрати) vs виведені кошти (профіт), джерело "Дима"</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.byYear.map((r) => ({ year: r.year, 'Витрати': r.expenseUsd, 'Профіт': r.profitUsd }))} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={50} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Витрати" fill={EXPENSE_COLOR} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Профіт" fill={PROFIT_COLOR} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Рік</TableCell>
                  <TableCell>Витрати (USD)</TableCell>
                  <TableCell>Профіт (USD)</TableCell>
                  <TableCell>Різниця (USD)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.byYear.map((row) => (
                  <TableRow key={row.year}>
                    <TableCell>{row.year}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{fmt(row.expenseUsd)}</TableCell>
                    <TableCell sx={{ color: 'success.main' }}>{fmt(row.profitUsd)}</TableCell>
                    <TableCell sx={{ color: row.diffUsd >= 0 ? 'success.main' : 'error.main' }}>{fmt(row.diffUsd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
